import { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { Lote } from '../../interfaces/inventory'
import { useProductDetailsContext } from '../../contexts/ProductDetailsContext'
import { useProductDetails } from '../../hooks/useProductDetails'
import { useDebounce } from '@/hooks/useDebounce'
import type { BatchesQueryParams } from '../../hooks/useProductDetails'

interface BatchWithExpiration {
  expirationAt?: string | null
}

interface LotesFilters {
  // UI state only; will be mapped to backend query params
  searchValue?: string
  entryDateFrom?: string
  entryDateTo?: string
  expiryDateFrom?: string
  expiryDateTo?: string
  // Filtros avanzados
  cantidadMin?: string
  cantidadMax?: string
  costoCompraMin?: string
  costoCompraMax?: string
  costoVentaMin?: string
  costoVentaMax?: string
  proveedorId?: string
  ubicacion?: string
  contenedor?: string
  contenedorId?: number
}

interface LotesContextType {
  lotes: Lote[]
  allLotes: Lote[] // Sin filtrar
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  refetch: () => void
  inventoryId: string
  filters: LotesFilters
  setFilters: React.Dispatch<React.SetStateAction<LotesFilters>>
  totalItems: number
  totalPages: number
}

const LotesContext = createContext<LotesContextType | undefined>(undefined)

interface LotesProviderProps {
  children: ReactNode
  inventoryId: string
}

export function LotesProvider({ children, inventoryId }: LotesProviderProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [filters, setFilters] = useState<LotesFilters>({})

  // Use the updated product details context to get batches only
  const { selectedProductId, productMainInfo } = useProductDetailsContext()

  // Debounce search value to avoid excessive API calls (500ms delay)
  const debouncedSearchValue = useDebounce(filters.searchValue || '', 500)

  const queryParams: BatchesQueryParams | undefined = useMemo(() => {
    if (!selectedProductId) return undefined

    const toDateString = (d?: string): string | null => {
      if (!d || d.trim() === '') return null
      return d
    }

    const supplierIdNum =
      filters.proveedorId && /^\d+$/.test(filters.proveedorId)
        ? Number(filters.proveedorId)
        : null
    const areaIdsParam =
      filters.ubicacion && /^\d+$/.test(filters.ubicacion)
        ? [Number(filters.ubicacion)]
        : null

    // Crear objeto de parámetros solo con valores que realmente tienen datos
    const params: BatchesQueryParams = {
      page: currentPage,
      limit: pageSize,
    }

    // Solo añadir parámetros si tienen valor para evitar re-renders innecesarios
    if (debouncedSearchValue && debouncedSearchValue.trim()) {
      params.search = debouncedSearchValue.trim()
    }

    const entryDateFrom = toDateString(filters.entryDateFrom)
    const entryDateTo = toDateString(filters.entryDateTo)
    const expirationDateFrom = toDateString(filters.expiryDateFrom)
    const expirationDateTo = toDateString(filters.expiryDateTo)

    if (entryDateFrom) params.entryDateFrom = entryDateFrom
    if (entryDateTo) params.entryDateTo = entryDateTo
    if (expirationDateFrom) params.expirationDateFrom = expirationDateFrom
    if (expirationDateTo) params.expirationDateTo = expirationDateTo

    if (filters.cantidadMin)
      params.availableQuantityFrom = Number(filters.cantidadMin)
    if (filters.cantidadMax)
      params.availableQuantityTo = Number(filters.cantidadMax)
    if (filters.costoCompraMin)
      params.purchaseCostFrom = Number(filters.costoCompraMin)
    if (filters.costoCompraMax)
      params.purchaseCostTo = Number(filters.costoCompraMax)
    if (filters.costoVentaMin)
      params.productPriceFrom = Number(filters.costoVentaMin)
    if (filters.costoVentaMax)
      params.productPriceTo = Number(filters.costoVentaMax)
    if (supplierIdNum) params.supplierId = supplierIdNum
    if (filters.contenedorId) params.buyedReceiptId = filters.contenedorId
    if (areaIdsParam) params.areaIds = areaIdsParam

    return params
  }, [
    selectedProductId,
    currentPage,
    pageSize,
    debouncedSearchValue,
    filters.entryDateFrom,
    filters.entryDateTo,
    filters.expiryDateFrom,
    filters.expiryDateTo,
    filters.cantidadMin,
    filters.cantidadMax,
    filters.costoCompraMin,
    filters.costoCompraMax,
    filters.costoVentaMin,
    filters.costoVentaMax,
    filters.proveedorId,
    filters.ubicacion,
    filters.contenedorId,
  ])

  const {
    data: productDetails,
    isLoading: isLoadingDetails,
    isFetching: isFetchingDetails,
    isError: isErrorDetails,
    refetch: refetchDetails,
  } = useProductDetails({
    productId: selectedProductId,
    params: queryParams,
    enabled: !!selectedProductId,
  })

  // Transform batches from product details into Lote format (usar datos del backend ya filtrados)
  const allLotes: Lote[] = useMemo(() => {
    // Si no hay batches del productDetails, no podemos mostrar lotes
    if (!productDetails?.batches) {
      return []
    }

    // Mapear todos los lotes según la nueva estructura del API
    return (
      productDetails.batches?.map((batch, index) => {
        const batchContainer = batch.container
        let containerName: string | undefined
        let containerObject:
          | { id: number; name: string; code?: string }
          | undefined

        if (typeof batchContainer === 'string') {
          containerName = batchContainer
        } else if (
          typeof batchContainer === 'object' &&
          batchContainer !== null
        ) {
          containerName = batchContainer.name
          containerObject = batchContainer
        }

        const batchExpiration: string | undefined =
          (batch as BatchWithExpiration).expirationAt ?? undefined

        interface QuantityObj {
          entryQuantity?: number
          availableQuantity?: number
        }

        const b = batch as unknown as {
          availableQuantity?: number
          entryQuantity?: number
          quantity?: number | QuantityObj
        }

        // Normalize available quantity - handle string or number
        let normalizedAvailable = 0
        if (typeof b.availableQuantity === 'number') {
          normalizedAvailable = b.availableQuantity
        } else if (typeof b.availableQuantity === 'string') {
          normalizedAvailable = parseFloat(b.availableQuantity) || 0
        } else if (b.quantity && typeof b.quantity === 'object') {
          const q = b.quantity as QuantityObj
          if (typeof q.availableQuantity === 'number') {
            normalizedAvailable = q.availableQuantity
          }
        } else if (typeof b.quantity === 'number') {
          normalizedAvailable = b.quantity
        }

        // Normalize entry quantity - handle string or number
        let normalizedEntry = normalizedAvailable
        if (typeof b.entryQuantity === 'number') {
          normalizedEntry = b.entryQuantity
        } else if (typeof b.entryQuantity === 'string') {
          normalizedEntry = parseFloat(b.entryQuantity) || normalizedAvailable
        } else if (b.quantity && typeof b.quantity === 'object') {
          const q = b.quantity as QuantityObj
          if (typeof q.entryQuantity === 'number') {
            normalizedEntry = q.entryQuantity
          }
        } else if (typeof b.quantity === 'number') {
          normalizedEntry = b.quantity
        }

        const loteObject = {
          // Core API fields
          id: batch.id?.toString() || `batch-${index}`,
          uniqueCode: batch.uniqueCode,
          availableQuantity: normalizedAvailable,
          entryQuantity: normalizedEntry,
          entryAt: batch.entryAt,
          supplier: batch.supplier, // Proveedor específico del lote
          locations: batch.locations,

          // Map to table-compatible fields
          productName:
            productMainInfo?.productName || productDetails.productName,
          productId: productMainInfo?.productId || productDetails.productId,
          inventoryItemId: inventoryId,

          // Quantity mapping for table compatibility
          // Keep a structured quantity object so components can use available vs entry
          quantity: {
            availableQuantity: normalizedAvailable,
            entryQuantity: normalizedEntry,
          },
          // totalQuantity represents the initial/entry amount (fallback to available)
          totalQuantity: normalizedEntry || normalizedAvailable,

          // Date mapping for table compatibility
          entryDate: batch.entryAt,

          // Cost fields from API
          netCost: batch.netCost || null,
          grossCost: batch.grossCost || null,
          registeredPrice: batch.registeredPrice || null,

          // Receipt information
          buyedReceipt: batch.buyedReceipt || null,

          // Stock areas
          stockAreas: batch.stockAreas || [],

          // Fields not provided by batch API (legacy)
          purchasePrice: undefined,
          sellingPrice: undefined,
          location: batch.locations?.[0] || undefined,

          containers: undefined, // No pasar contenedores del inventario general
          container: containerName || undefined, // Solo si el batch específico tiene contenedor
          assignedContainer: containerObject || undefined, // Solo si el batch tiene objeto contenedor específico
          suppliers: undefined, // NO pasar proveedores del inventario general - solo usar batch.supplier
          expirations: batchExpiration ? [batchExpiration as string] : [],
          expirationDate: (batchExpiration as string) || undefined,
        }

        return loteObject
      }) || []
    )
  }, [
    productDetails,
    inventoryId,
    productMainInfo?.productName,
    productMainInfo?.productId,
  ])

  // No aplicar filtros en frontend: el backend devuelve ya filtrado
  const filteredLotes = allLotes

  // Calcular información de paginación del servidor
  const totalItems = productDetails?.total || 0
  const totalPages = Math.ceil(totalItems / pageSize)

  return (
    <LotesContext.Provider
      value={{
        lotes: filteredLotes, // Lotes filtrados
        allLotes, // Todos los lotes sin filtrar
        isLoading: isLoadingDetails,
        isFetching: isFetchingDetails,
        isError: isErrorDetails,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        refetch: refetchDetails,
        inventoryId,
        filters,
        setFilters,
        totalItems,
        totalPages,
      }}
    >
      {children}
    </LotesContext.Provider>
  )
}

export function useLotes() {
  const context = useContext(LotesContext)
  if (context === undefined) {
    throw new Error('useLotes must be used within a LotesProvider')
  }
  return context
}
